import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  DialogActions,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RepertoireData, RepertoireReducer, UserRepertoireData } from 'Types/RepertoireTypes';
import { Menu } from 'Components/RoadmapComponents/Menu';
import PageNav from './PageNav';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import * as repertoireActions from '../../Actions/repertoire';

const CREATED_REPERTOIRE_SOURCE = 'created';
const CREATED_REPERTOIRE_AUTHOR = 'you';

const upsertById = <T extends { id: number }>(items: T[], nextItem: T) => [
  nextItem,
  ...items.filter((item) => item.id !== nextItem.id),
];

const Library = () => {
  const dispatch = useDispatch();
  const { currentAuthToken } = useAuthWrapperContext();
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const userRepertoireData = repertoireReducerData?.userRepertoireData || [];
  const createdRepertoireData = userRepertoireData.filter(
    (rep) => rep.source === CREATED_REPERTOIRE_SOURCE,
  );
  const uploadedRepertoireData = userRepertoireData.filter(
    (rep) => rep.source !== CREATED_REPERTOIRE_SOURCE,
  );
  const hasUserRepertoire = userRepertoireData.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [_uploading, _setUploading] = useState(false);

  useEffect(() => {
    // Fetch user repertoire on mount
    fetchUserRepertoire();
  }, []);

  useEffect(() => {
    // Check for processing repertoire and poll for updates
    if (!userRepertoireData || !Array.isArray(userRepertoireData)) {
      return;
    }

    const processingItems = userRepertoireData.filter((rep) => rep.status === 'processing');

    if (processingItems.length > 0) {
      const interval = setInterval(() => {
        checkProcessingStatus(processingItems.map((item) => item.id));
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [userRepertoireData]);

  const fetchUserRepertoire = async () => {
    try {
      const token = await currentAuthToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      dispatch(repertoireActions.fetchUserRepertoireSuccess(response.data));
    } catch (error) {
      console.error('Error fetching user repertoire:', error);
    }
  };

  const checkProcessingStatus = async (ids: number[]) => {
    try {
      const token = await currentAuthToken();
      let shouldRefreshUserRepertoire = false;
      for (const id of ids) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.data.status !== 'processing') {
          shouldRefreshUserRepertoire = true;
          dispatch(
            repertoireActions.setUserRepertoireProcessing({
              id,
              status: response.data.status,
            }),
          );
        }
      }
      if (shouldRefreshUserRepertoire) {
        await fetchUserRepertoire();
      }
    } catch (error) {
      console.error('Error checking processing status:', error);
    }
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Menu selected={'Repertoire'} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
        }}
      >
        <PageNav selected={'Library'} />
        <Box
          sx={{
            margin: '0px 0px',
            justifyContent: 'flex-start',
            height: 'calc(100vh - 60px)',
            overflowY: 'auto',
            position: 'relative',
          }}
          ref={containerRef}
        >
          {!hasUserRepertoire ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenUploadModal}
                sx={{
                  backgroundColor: '#469F6A',
                  color: 'white',
                  textTransform: 'uppercase',
                  fontWeight: '700',
                  fontSize: '15px',
                  padding: '12px 32px',
                  '&:hover': {
                    backgroundColor: '#3a8556',
                  },
                }}
              >
                Upload Repertoire
              </Button>
            </Box>
          ) : (
            <>
              {uploadedRepertoireData.length > 0 && (
                <RepertoireCardGrid topPadding={28}>
                  {uploadedRepertoireData.map((rep) => (
                    <UserRepertoireItem
                      key={rep.id}
                      repertoireData={rep}
                      onDelete={fetchUserRepertoire}
                    />
                  ))}
                </RepertoireCardGrid>
              )}
              {createdRepertoireData.length > 0 && (
                <>
                  <Box
                    sx={{
                      padding: uploadedRepertoireData.length > 0 ? '4px 28px 0' : '28px 28px 0',
                    }}
                  >
                    <Box sx={createdRepertoireDividerStyles}>
                      <Box sx={createdRepertoireDividerLineStyles} />
                      <Box sx={createdRepertoireDividerLabelStyles}>Your Created Repertoire</Box>
                      <Box sx={createdRepertoireDividerLineStyles} />
                    </Box>
                  </Box>
                  <RepertoireCardGrid topPadding={18}>
                    {createdRepertoireData.map((rep) => (
                      <UserRepertoireItem
                        key={rep.id}
                        repertoireData={rep}
                        onDelete={fetchUserRepertoire}
                      />
                    ))}
                  </RepertoireCardGrid>
                </>
              )}
              <IconButton
                onClick={handleOpenUploadModal}
                sx={{
                  position: 'fixed',
                  bottom: '32px',
                  right: '32px',
                  backgroundColor: '#469F6A',
                  color: 'white',
                  width: '56px',
                  height: '56px',
                  '&:hover': {
                    backgroundColor: '#3a8556',
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <UploadModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onSuccess={fetchUserRepertoire}
      />
    </Box>
  );
};

export default Library;

const RepertoireCardGrid = ({
  children,
  topPadding,
}: {
  children: React.ReactNode;
  topPadding: number;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '20px',
      padding: `${topPadding}px 28px 28px`,
    }}
  >
    {children}
  </Box>
);

const createdRepertoireDividerStyles = {
  display: 'grid',
  gridTemplateColumns: 'minmax(24px, 1fr) auto minmax(24px, 1fr)',
  alignItems: 'center',
  gap: '14px',
};

const createdRepertoireDividerLineStyles = {
  height: '1px',
  backgroundColor: 'rgba(173, 177, 183, 0.28)',
};

const createdRepertoireDividerLabelStyles = {
  color: '#ADB1B7',
  fontFamily: 'Lato',
  fontSize: '12px',
  fontWeight: 800,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

interface UserRepertoireItemProps {
  repertoireData: UserRepertoireData;
  onDelete: () => void;
}

const UserRepertoireItem = ({ repertoireData, onDelete }: UserRepertoireItemProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAuthToken } = useAuthWrapperContext();
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const isProcessing = repertoireData.status === 'processing';
  const isError = repertoireData.status === 'failed' || repertoireData.status === 'error';
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getArtistOrComposerName = () => {
    if (repertoireData.source === CREATED_REPERTOIRE_SOURCE) {
      return CREATED_REPERTOIRE_AUTHOR;
    }
    if (repertoireData.artists && repertoireData.artists.length > 0) {
      return repertoireData.artists.map((a) => a.name).join(', ');
    }
    if (repertoireData.composers && repertoireData.composers.length > 0) {
      return repertoireData.composers.map((c) => c.name).join(', ');
    }
    return 'Unknown';
  };

  const handleClick = () => {
    if (!isProcessing && !isError) {
      dispatch(
        repertoireActions.fetchRepertoireDataSuccess(
          upsertById(
            repertoireReducerData.repertoireData ?? [],
            repertoireData as unknown as RepertoireData,
          ),
        ),
      );
      dispatch(repertoireActions.setRepertoireSelect({ repertoireIdSelect: repertoireData.id }));
      setTimeout(() => {
        navigate('/repertoire-play');
      }, 150);
    }
  };

  const handleReupload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReuploadModal(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = await currentAuthToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire/${repertoireData.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setShowDeleteModal(false);
      onDelete(); // Call the parent's refresh function
    } catch (error) {
      console.error('Failed to delete repertoire:', error);
      // Could add error handling here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          minWidth: '160px',
          maxWidth: '160px',
          height: '180px',
          backgroundColor: '#07111D',
          borderRadius: '6px',
          border: isError ? '2px solid #C24444' : '2px solid #767C8C',
          position: 'relative',
          cursor: isProcessing || isError ? 'default' : 'pointer',
          opacity: isProcessing || isError ? 0.7 : 1,
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Delete Icon - appears on hover */}
        {isHovered && !isProcessing && (
          <IconButton
            onClick={handleDeleteClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#C24444',
              width: '32px',
              height: '32px',
              '&:hover': {
                backgroundColor: 'rgba(196, 68, 68, 0.2)',
                color: '#ff6b6b',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            width: '100%',
            height: '100px',
            backgroundColor: 'white',
            position: 'relative',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottom: '2px solid #767C8C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isProcessing ? (
            <CircularProgress size={40} sx={{ color: '#469F6A' }} />
          ) : isError ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: '8px',
              }}
            >
              <Box sx={{ color: '#C24444', fontSize: '12px', textAlign: 'center' }}>
                Upload Failed
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleReupload}
                sx={{
                  backgroundColor: '#C24444',
                  color: 'white',
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  padding: '4px 8px',
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: '#a03737',
                  },
                }}
              >
                Reupload
              </Button>
            </Box>
          ) : (
            repertoireData.repertoire_png_preview_url && (
              <img
                style={{ maxWidth: '94%', maxHeight: '100%' }}
                src={repertoireData.repertoire_png_preview_url}
                alt={repertoireData.name}
              />
            )
          )}
        </Box>
        <Box
          sx={{
            height: '74px',
            paddingBottom: '6px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingLeft: '8px',
            paddingRight: '6px',
          }}
        >
          <Box
            sx={{
              marginTop: '5px',
              marginBottom: '7px',
              color: '#FFFFFF',
              fontFamily: 'Lato',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '17px',
              letterSpacing: '0.01em',
              textAlign: 'left',
            }}
          >
            {repertoireData.name}
          </Box>
          <Box
            sx={{
              marginBottom: '2px',
              color: '#FFFFFF',
              fontFamily: 'Lato',
              fontSize: '14px',
              fontStyle: 'italic',
              fontWeight: '700',
              lineHeight: '17px',
              letterSpacing: '0.01em',
              textAlign: 'left',
              textOverflow: 'ellipsis',
              maxWidth: '140px',
              maxHeight: '30px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {getArtistOrComposerName()}
          </Box>
        </Box>
      </Box>
      {showReuploadModal && (
        <UploadModal
          open={showReuploadModal}
          onClose={() => setShowReuploadModal(false)}
          onSuccess={() => {
            setShowReuploadModal(false);
            window.location.reload();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#050B13',
            border: '2px solid #C24444',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#FFFFFF',
            fontFamily: 'Lato',
            fontSize: '18px',
            fontWeight: '700',
            textAlign: 'center',
          }}
        >
          Delete Repertoire
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#FFFFFF', textAlign: 'center', padding: '16px 0' }}>
            Are you sure you want to delete "{repertoireData.name}"? This action cannot be undone.
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '16px', padding: '16px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              color: '#FFFFFF',
              borderColor: '#767C8C',
              textTransform: 'uppercase',
              fontWeight: '600',
              '&:hover': {
                borderColor: '#469F6A',
                backgroundColor: 'rgba(70, 159, 106, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isDeleting}
            sx={{
              backgroundColor: '#C24444',
              color: 'white',
              textTransform: 'uppercase',
              fontWeight: '600',
              '&:hover': {
                backgroundColor: '#a03737',
              },
              '&:disabled': {
                backgroundColor: '#767C8C',
                color: '#ADB1B7',
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadModal = ({ open, onClose, onSuccess }: UploadModalProps) => {
  const { currentAuthToken } = useAuthWrapperContext();
  const [formData, setFormData] = useState({
    file: null as File | null,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate MusicXML file
      if (
        !file.name.endsWith('.xml') &&
        !file.name.endsWith('.musicxml') &&
        !file.name.endsWith('.mxml') &&
        !file.name.endsWith('.mxl')
      ) {
        setError('Please upload a valid MusicXML file (.xml, .musicxml, .mxml, or .mxl)');
        return;
      }
      setFormData((prev) => ({ ...prev, file }));
      setError('');
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = await currentAuthToken();
      const uploadFormData = new FormData();

      uploadFormData.append('musicXml', formData.file);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      // Reset form
      setFormData({
        file: null,
      });
      setError('');

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to upload repertoire');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#050B13',
          border: '2px solid #469F6A',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          fontFamily: 'Lato',
          fontSize: '18px',
          fontWeight: '700',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        Upload Repertoire
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#FFFFFF',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px' }}>
          <Box>
            <input
              type="file"
              accept=".xml,.mxl,.musicxml,.mxml"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{
                  color: '#FFFFFF',
                  borderColor: '#767C8C',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#469F6A',
                    backgroundColor: 'rgba(70, 159, 106, 0.1)',
                  },
                }}
              >
                {formData.file ? formData.file.name : 'Choose MusicXML File'}
              </Button>
            </label>
          </Box>
          {error && (
            <Box sx={{ color: '#C24444', fontSize: '14px', textAlign: 'center' }}>{error}</Box>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading}
            sx={{
              backgroundColor: '#469F6A',
              color: 'white',
              textTransform: 'uppercase',
              fontWeight: '700',
              fontSize: '15px',
              padding: '12px 32px',
              '&:hover': {
                backgroundColor: '#3a8556',
              },
              '&:disabled': {
                backgroundColor: '#767C8C',
                color: '#ADB1B7',
              },
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
